// SplashPage.jsx
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Stack,
  Typography,
  Paper,
} from '@mui/material';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import CloudOutlinedIcon from '@mui/icons-material/CloudOutlined';
import { useNavigate } from 'react-router-dom';

const ACCENT = 'rgb(219, 234, 254)';
const ACCENT_BORDER = '#60A5FA';
const HEADER_BG = '#1e40af';

const features = [
  {
    title: 'More realistic simulation',
    text: 'Participants interact with a working EPR-style system, requesting and interpreting results as they would in real clinical practice.',
    icon: <MonitorHeartOutlinedIcon />,
  },
  {
    title: 'Fast EPR creation',
    text: 'Build complete simulated patient records including documents, observations, medications, bloods, and imaging.',
    icon: <DescriptionOutlinedIcon />,
  },
  {
    title: 'Generate clinical data',
    text: 'Create documents, results, and scenario content quickly using built-in generation tools, then edit as needed.',
    icon: <AutoAwesomeOutlinedIcon />,
  },
  {
    title: 'Track participant actions',
    text: 'Capture requests, interventions, and decisions during the session to support structured debrief and feedback.',
    icon: <TimelineOutlinedIcon />,
  },
];

export default function SplashPage() {
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFF' }}>

      {/* HEADER (sticky) */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          height: 72,
          px: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: HEADER_BG,
          color: 'white',
          borderBottom: `1px solid ${ACCENT_BORDER}`,
        }}
      >
        <img src="/ssiconfull.png" alt="logo" style={{ width: 160 }} />

        <Stack direction="row" spacing={1.5}>
          <Button
            variant="outlined"
            onClick={() => navigate('/app')}
            sx={{
              textTransform: 'none',
              color: 'white',
              borderColor: 'rgba(255,255,255,0.5)',
            }}
          >
            Log in
          </Button>

          <Button
            variant="contained"
            onClick={() => navigate('/signup')}
            sx={{ textTransform: 'none' }}
          >
            Get started
          </Button>
        </Stack>
      </Box>

      {/* HERO */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 2 } }}>
        <Stack spacing={4} alignItems="center" textAlign="center">
          <Typography
            sx={{
              fontWeight: 900,
              fontSize: { xs: 36, md: 60 },
              lineHeight: 1.1,
              maxWidth: 900,
            }}
          >
            Build immersive clinical simulations with a live EPR system.
          </Typography>

          <Typography
            color="text.secondary"
            sx={{
              fontSize: { xs: 17, md: 20 },
              maxWidth: 760,
              lineHeight: 1.6,
            }}
          >
            Create simulated patient records, run live scenarios, send results in real time,
            and capture participant actions for structured feedback.
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/signup')}
              sx={{ textTransform: 'none', px: 4 }}
            >
              Start building scenarios
            </Button>

            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/app')}
              sx={{ textTransform: 'none', px: 4 }}
            >
              Log in
            </Button>
          </Stack>
        </Stack>

        {/* FEATURES */}
        <Box
          sx={{
            mt: 8,
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              lg: 'repeat(4, minmax(0, 1fr))',
            },
            gap: 2,
          }}
        >
          {features.map((f) => (
            <Card
              key={f.title}
              elevation={0}
              sx={{
                border: '1px solid #d1d5db',
                borderRadius: 3,
                bgcolor: 'white',
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    bgcolor: ACCENT,
                    border: `1px solid ${ACCENT_BORDER}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                  }}
                >
                  {f.icon}
                </Box>

                <Typography sx={{ fontWeight: 800, mb: 1 }}>
                  {f.title}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  {f.text}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* NO INSTALL SECTION */}
        <Paper
          elevation={0}
          sx={{
            mt: 10,
            border: `1px solid ${ACCENT_BORDER}`,
            borderRadius: 4,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              p: { xs: 3, md: 5 },
              bgcolor: ACCENT,
              display: 'flex',
              gap: 3,
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 3,
                bgcolor: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CloudOutlinedIcon />
            </Box>

            <Box>
              <Typography sx={{ fontWeight: 900, fontSize: { xs: 24, md: 34 }, mb: 1 }}>
                No installation required
              </Typography>

              <Typography color="text.secondary">
                Runs entirely in the browser with facilitator and participant views.
                No setup, no installs, no friction.
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* PRICING */}
        <Box sx={{ mt: 10, textAlign: 'center' }}>
          <Typography sx={{ fontWeight: 900, fontSize: { xs: 28, md: 42 }, mb: 1 }}>
            Simple pricing
          </Typography>

          <Typography color="text.secondary" sx={{ maxWidth: 700, mx: 'auto' }}>
            Flexible pricing designed for individuals, medical schools, and simulation teams.
          </Typography>
        </Box>

        <Box
          sx={{
            mt: 4,
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'repeat(3, minmax(0, 1fr))',
            },
            gap: 2,
          }}
        >
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography sx={{ fontWeight: 800 }}>Individual</Typography>
              <Typography color="text.secondary" sx={{ my: 1 }}>
                For students and small-scale teaching.
              </Typography>
              <Typography sx={{ fontWeight: 900, fontSize: 24 }}>
                Coming soon
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ borderRadius: 3, border: `2px solid ${ACCENT_BORDER}` }}>
            <CardContent>
              <Chip label="Most relevant" size="small" sx={{ mb: 1 }} />
              <Typography sx={{ fontWeight: 800 }}>
                Medical schools & hospitals
              </Typography>
              <Typography color="text.secondary" sx={{ my: 1 }}>
                Full simulation suite with live sessions and tracking.
              </Typography>
              <Typography sx={{ fontWeight: 900, fontSize: 24 }}>
                Contact for pricing
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography sx={{ fontWeight: 800 }}>Custom</Typography>
              <Typography color="text.secondary" sx={{ my: 1 }}>
                Tailored deployments and scenario packs.
              </Typography>
              <Typography sx={{ fontWeight: 900, fontSize: 24 }}>
                Bespoke
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Container>

      <div style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#F8FAFF',
        height: '20px'
      }}>
        <Typography sx={{ fontSize: 12 }}>
          © 2026, SimSuite. All rights reserved.  
        </Typography>

        <Button
          href="/credits/image"
          target="_blank"
          rel="noopener noreferrer"
          disableRipple
          sx={{
            fontSize: 12,
            minWidth: 'auto',
            padding: 0,
            marginLeft: 1,
            textTransform: 'none',
            '&:hover': {
              backgroundColor: 'transparent',
            }
          }}
        >
          Image Credits
        </Button>
        <Button
          href="/terms"
          target="_blank"
          rel="noopener noreferrer"
          disableRipple
          sx={{
            fontSize: 12,
            minWidth: 'auto',
            padding: 0,
            marginLeft: 1,
            textTransform: 'none',
            '&:hover': {
              backgroundColor: 'transparent',
            }
          }}
        >
          Terms of Use
        </Button>
        <Button
          href="/privacy"
          target="_blank"
          rel="noopener noreferrer"
          disableRipple
          sx={{
            fontSize: 12,
            minWidth: 'auto',
            padding: 0,
            marginLeft: 1,
            textTransform: 'none',
            '&:hover': {
              backgroundColor: 'transparent',
            }
          }}
        >
          Privacy policy
        </Button>
      </div>

    </Box>
  );
}